import { DATA_VIZ_ICONS } from './icons'
import { PATH, ROUTE_NAME } from '~/constants/strings'

export const ODataVisualizationRouteNames = {
  Employment: 'employment',
  Goals: 'goals',
  AdverseImpact: 'adverse-impact',
  PersonnelActivity: 'personnel-activity',
  GoalAttainment: 'goal-attainment',
  ApplicantFlow: 'applicant-flow',
  Overview: 'overview',
  ProtectedVeterans: 'protected-veterans',
  IndividualsWithDisabilities: 'individuals-with-disabilities',

  ProjectOverview: ROUTE_NAME.PROJECT_OVERVIEW_DATA_VISUALIZATIONS,
  CompetitiveMovements: ROUTE_NAME.COMPETITIVE_MOVEMENTS_DATA_VISUALIZATIONS,
  NonCompetitiveMovements:
    ROUTE_NAME.NON_COMPETITIVE_MOVEMENTS_DATA_VISUALIZATIONS,
  RecruitmentProcessInsights:
    ROUTE_NAME.RECRUITMENT_PROCESS_INSIGHTS_DATA_VISUALIZATIONS,
  WorkforceInsights: ROUTE_NAME.WORKFORCE_INSIGHTS_DATA_VISUALIZATIONS,
  TalentAvailabilityMetrics:
    ROUTE_NAME.TALENT_AVAILABILITY_METRICS_DATA_VISUALIZATIONS,
} as const

export type TDataVisualizationRouteNames =
  (typeof ODataVisualizationRouteNames)[keyof typeof ODataVisualizationRouteNames]

export const DATA_VISUALIZATION_CONFIGS: Record<
  TDataVisualizationRouteNames,
  { text: string; iconName: string; description?: string }
> = {
  [ODataVisualizationRouteNames.Employment]: {
    text: 'Employment',
    iconName: DATA_VIZ_ICONS.EMPLOYMENT,
  },
  [ODataVisualizationRouteNames.Goals]: {
    text: 'Goals',
    iconName: DATA_VIZ_ICONS.GOALS,
  },
  [ODataVisualizationRouteNames.AdverseImpact]: {
    text: 'Adverse Impact',
    iconName: DATA_VIZ_ICONS.ADVERSE_IMPACT,
  },
  [ODataVisualizationRouteNames.PersonnelActivity]: {
    text: 'Personnel Activity',
    iconName: DATA_VIZ_ICONS.PERSONNEL_ACTIVITY,
  },
  [ODataVisualizationRouteNames.GoalAttainment]: {
    text: 'Goal Attainment',
    iconName: DATA_VIZ_ICONS.GOAL_ATTAINMENT,
  },
  [ODataVisualizationRouteNames.ApplicantFlow]: {
    text: 'Applicant Flow',
    iconName: DATA_VIZ_ICONS.APPLICANT_FLOW,
  },
  [ODataVisualizationRouteNames.Overview]: {
    text: 'Overview',
    iconName: DATA_VIZ_ICONS.OVERVIEW,
  },
  [ODataVisualizationRouteNames.ProtectedVeterans]: {
    text: 'Protected Veterans',
    iconName: DATA_VIZ_ICONS.PROTECTED_VETERANS,
  },
  [ODataVisualizationRouteNames.IndividualsWithDisabilities]: {
    text: 'Individuals With Disabilities',
    iconName: DATA_VIZ_ICONS.IWD,
  },

  // WFA Dataviz
  [ODataVisualizationRouteNames.ProjectOverview]: {
    text: 'Overview',
    iconName: DATA_VIZ_ICONS.OVERVIEW,
  },
  [ODataVisualizationRouteNames.WorkforceInsights]: {
    text: 'Workforce Insights',
    iconName: DATA_VIZ_ICONS.WORKFORCE_REPRESENTATION,
    description:
      'Using a snapshot of all active employees, workforce metrics establish a current baseline for analysis.  Promotions include competitive promotions to and non-competitive promotions from a strata.',
  },
  [ODataVisualizationRouteNames.RecruitmentProcessInsights]: {
    text: 'Recruitment Process Insights',
    iconName: DATA_VIZ_ICONS.PROJECT_APPLICANT_FLOW,
    description: 'Assessment of applicant recordkeeping practices.',
  },
  [ODataVisualizationRouteNames.CompetitiveMovements]: {
    text: 'Competitive Movements',
    iconName: DATA_VIZ_ICONS.SELECTION_RATE_ANALYSES,
    description:
      'A selection rate analysis is used to analyze selections that are competitive or where there is a clearly defined pool. This analysis compares the selection rates of the focal group to the reference group to identify if there are any situations where a group is selected at a lower rate than expected.',
  },
  [ODataVisualizationRouteNames.NonCompetitiveMovements]: {
    text: 'Non-Competitive Movements',
    iconName: DATA_VIZ_ICONS.PROPORTIONAL_ANALYSES,
    description:
      "A proportional analysis is used to analyze selections where there is no clearly defined pool. A proportional analysis compares the selection percentage for each demographic group to that group's percentage in the available pool. This approach helps determine if outcomes are higher or lower than would be expected based on workforce representation. The available pool was approximated by identifying those in the strata from which the personnel action occurred at any point during the analyzed period.",
  },
  [ODataVisualizationRouteNames.TalentAvailabilityMetrics]: {
    text: 'Talent Availability Metrics',
    iconName: DATA_VIZ_ICONS.LABOR_MARKET_COMPARISONS,
    description:
      'Applicant pool and workforce composition was evaluated against external census data benchmarks to determine whether recruitment efforts are effective. This practice helps identify any outreach gaps or opportunity barriers for qualified candidates from all backgrounds.',
  },
}

// pages that do not have default service lines or side navigation
export const DESELECT_SERVICE_LINES_PATHS: string[] = [
  // Document Center
  PATH.DOCUMENT_CENTER,
  PATH.DOCUMENT_CENTER_PUBLIC_UPLOAD,

  // Resource Library
  PATH.RESOURCE_LIBRARY,
  PATH.RESOURCE_LIBRARY_PREVIEW,

  // Administration
  PATH.CLIENT_SUPPORT_ACCESS,
  PATH.ADMIN_SUPPORT_ACCESS,
  PATH.CONTACT_SUPPORT,
  PATH.CLIENT_MANAGEMENT,
  PATH.USER_MANAGEMENT,
  PATH.CREATE_CLIENT,
  PATH.EDIT_CLIENT,
  PATH.CREATE_USER,
  PATH.EDIT_USER,
  PATH.MANAGE_SECURITY,
  PATH.MANAGE_SYSTEM_ALERTS,
  PATH.SECURITY_SETTINGS,
  PATH.MANAGE_FEATURE_FLAGS,
  PATH.MANAGE_PROJECTS_LOCATIONS,

  // Other
  PATH.ROOT,

  // User Onboarding
  PATH.SSO_FAILED,
  PATH.PASSWORD_EXPIRED,
  PATH.RESET_PASSWORD,
  PATH.WELCOME,

  // User Services
  PATH.USER_PROFILE,
  PATH.VIEW_AS,
  PATH.NOTIFICATIONS,
  PATH.SWITCH_CLIENT,
]
